// src/utils/helpers/urlUtils.js
// *******************************************************************************
// URL UTILITIES
// *******************************************************************************
// Métodos utilitários para manipulação de URLs
// Compatível com componentes React e funções simples
// *******************************************************************************

// *******************************************************************************
// GET METHODS
// *******************************************************************************
import useZustand from 'hooks/useZustand';

/**
 * Retorna a URL completa atual
 * @param {string} [customUrl] - URL customizada (opcional, para uso fora do browser)
 * @returns {string} URL completa
 */
const getUrl = (customUrl = null) => {
  if (customUrl) return customUrl;
  if (typeof window !== 'undefined' && window.location) {
    return window.location.href;
  }
  return '';
};

/**
 * Retorna o domínio da URL
 * @param {string} [customUrl] - URL customizada (opcional)
 * @returns {string} Domínio (ex: "https://example.com")
 */
const getDomain = (customUrl = null) => {
  const url = customUrl || getUrl();
  if (!url) return '';

  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}`;
  } catch {
    return '';
  }
};

/**
 * Retorna o endpoint (pathname) da URL
 * @param {string} [customUrl] - URL customizada (opcional)
 * @returns {string} Endpoint (ex: "/app/account/settings")
 */
const getUrlEndpoint = (customUrl = null) => {
  const url = customUrl || getUrl();
  if (!url) return '';

  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    return '';
  }
};

/**
 * Retorna a query string como objeto
 * Decodifica automaticamente arrays e objetos
 * @param {string} [customUrl] - URL customizada (opcional)
 * @returns {object} Query string como objeto
 */
const getQuery = (customUrl = null) => {
  const url = customUrl || getUrl();
  if (!url) return {};

  try {
    const urlObj = new URL(url);
    return decodeQuery(urlObj.search);
  } catch {
    return {};
  }
};

/**
 * Extrai parâmetros de uma URL baseado em um schema
 * @param {string} url - URL completa (ex: "/app/account/settings/organizations/68ee7157e31b105a1dade33b?tab=record")
 * @param {string} schemaUrl - Schema da URL com parâmetros prefixados com ":" (ex: "/app/account/settings/:path/:_id")
 * @returns {object} Objeto com parâmetros extraídos (ex: {path: "organizations", _id: "68ee7157e31b105a1dade33b"})
 */
const getParams = (url, schemaUrl) => {
  if (!url || !schemaUrl) return {};

  // Remove query string da URL (tudo após "?")
  const cleanUrl = url.split('?')[0];

  // Divide ambas as URLs em segmentos
  const urlSegments = cleanUrl.split('/').filter(segment => segment);
  const schemaSegments = schemaUrl.split('/').filter(segment => segment);

  // Verifica se o número de segmentos corresponde
  if (urlSegments.length !== schemaSegments.length) {
    return {};
  }

  // Extrai parâmetros
  const params = {};
  schemaSegments.forEach((schemaSegment, index) => {
    if (schemaSegment.startsWith(':')) {
      const paramName = schemaSegment.slice(1); // Remove o ":"
      params[paramName] = urlSegments[index];
    }
  });

  return params;
};

// *******************************************************************************
// REMOVE METHODS
// *******************************************************************************

/**
 * Remove todos os parâmetros de URL que começam com ":"
 * @param {string} url - URL com parâmetros (ex: "/v2/entities/:_id/details/:type")
 * @returns {string} URL sem parâmetros (ex: "/v2/entities")
 */
const removeUrlParams = url => {
  if (!url || typeof url !== 'string') return url || '';

  // Divide a URL em segmentos
  const segments = url.split('/');

  // Filtra segmentos até encontrar o primeiro parâmetro
  const cleanSegments = [];
  for (const segment of segments) {
    // Se encontrar um parâmetro (começa com :), para de adicionar segmentos
    if (segment.startsWith(':')) {
      break;
    }
    cleanSegments.push(segment);
  }

  // Reconstrói a URL
  const result = cleanSegments.join('/');

  // Garante que URLs que começavam com / continuem começando com /
  // mas evita dupla barra //
  if (url.startsWith('/') && !result.startsWith('/')) {
    return '/' + result;
  }

  return result || '/';
};

// *******************************************************************************
// UPDATE METHODS
// *******************************************************************************

/**
 * Atualiza a URL completa do navegador
 * @param {string} newUrl - Nova URL completa (ex: "/app/account/settings/123?tab=record")
 * @param {boolean} [replace=false] - Se true, substitui a URL no histórico
 * @returns {string} Nova URL
 */
const updateUrl = (newUrl, replace = false) => {
  if (typeof window === 'undefined' || !window.location) {
    console.warn('updateUrl: window.location não disponível');
    return '';
  }

  if (!newUrl || typeof newUrl !== 'string') {
    console.warn('updateUrl: invalid newUrl');
    return '';
  }

  // Atualiza a URL no browser
  if (window.history) {
    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }

  return newUrl;
};

/**
 * Atualiza a query string da URL atual
 * @param {object} queryObj - Objeto com parâmetros de query
 * @param {boolean} [replace=false] - Se true, substitui a URL no histórico
 * @returns {string} Nova URL completa
 */
const updateQuery = (queryObj = {}, replace = false) => {
  if (typeof window === 'undefined' || !window.location) {
    console.warn('updateQuery: window.location não disponível');
    return '';
  }

  const domain = getDomain();
  const endpoint = getUrlEndpoint();
  const encodedQuery = encodeQuery(queryObj);
  const newUrl = `${domain}${endpoint}${encodedQuery ? '?' + encodedQuery : ''}`;

  // Atualiza a URL no browser
  if (window.history) {
    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }

  return newUrl;
};

/**
 * Atualiza o endpoint da URL atual
 * Garante que o endpoint sempre começa com "/"
 * @param {string} endpoint - Novo endpoint (ex: "/brazil/campinas")
 * @param {boolean} [replace=false] - Se true, substitui a URL no histórico
 * @returns {string} Nova URL completa
 */
const updateEndpoint = (endpoint, replace = false) => {
  if (typeof window === 'undefined' || !window.location) {
    console.warn('updateEndpoint: window.location não disponível');
    return '';
  }

  // Garante que o endpoint começa com "/"
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  const domain = getDomain();
  const currentQuery = getQuery();
  const encodedQuery = encodeQuery(currentQuery);
  const newUrl = `${domain}${normalizedEndpoint}${encodedQuery ? '?' + encodedQuery : ''}`;

  // Atualiza a URL no browser
  if (window.history) {
    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }

  return newUrl;
};

/**
 * Parse URL with params and query parameters
 * @param {string} url - URL template with :param placeholders (ex: "/app/account/:path/:_id")
 * @param {string} [endpoint] - Optional endpoint to append to url (ex: "/:_id")
 * @param {object} [params] - Object with param replacements (ex: {path: "settings", _id: "123"})
 * @param {object} [query] - Object with query parameters (ex: {tab: "record"})
 * @returns {string} - Parsed URL with params and query string (ex: "/app/account/settings/123?tab=record")
 */
const mergeUrlParamsQuery = ({ url, endpoint = null, params = {}, query = {} }) => {
  if (!url || typeof url !== 'string') return url;

  // Build the full template URL
  // If endpoint is provided, append it to url
  // If not, use url itself as the template (it may already contain :param placeholders)
  let templateUrl = endpoint ? url + endpoint : url;

  let result = templateUrl;

  // First pass: Replace optional params (:param?) - must be done before regular params
  // to avoid leaving the ? behind
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      // Replace :param? with value (removes both the placeholder and the ?)
      result = result.replace(`:${key}?`, String(value));
    }
  });

  // Second pass: Replace regular params (:param)
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      result = result.replace(`:${key}`, String(value));
    }
  });

  // Clean up any remaining optional param placeholders that weren't provided
  // e.g., :userId? when userId wasn't in params - remove entirely
  result = result.replace(/:\w+\?/g, '');

  // Clean up any double slashes that may result from empty param removal (except after protocol)
  result = result.replace(/([^:])\/\/+/g, '$1/');

  // Add query parameters if provided
  if (query && Object.keys(query).length > 0) {
    const searchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        searchParams.append(key, stringValue);
      }
    });

    const queryString = searchParams.toString();
    if (queryString) {
      result += (result.includes('?') ? '&' : '?') + queryString;
    }
  }

  return result;
};

// *******************************************************************************
// ENCODE/DECODE METHODS
// *******************************************************************************

/**
 * Codifica query string a partir de string ou objeto
 * Suporta arrays e objetos aninhados
 * @param {string|object} input - String ou objeto para codificar
 * @returns {string} Query string codificada (sem o "?")
 */
const encodeQuery = input => {
  if (!input) return '';

  // Se for string, assume que já está codificada ou é um valor simples
  if (typeof input === 'string') {
    return input.startsWith('?') ? input.slice(1) : input;
  }

  // Se for objeto, codifica cada propriedade
  if (typeof input === 'object') {
    const searchParams = new URLSearchParams();

    Object.entries(input).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      // Se for array ou objeto, converte para JSON antes de codificar
      if (typeof value === 'object') {
        searchParams.append(key, JSON.stringify(value));
      } else {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  }

  return '';
};

/**
 * Decodifica query string para objeto
 * Decodifica automaticamente arrays e objetos JSON
 * @param {string} queryString - Query string para decodificar (com ou sem "?")
 * @returns {object} Objeto com parâmetros decodificados
 */
const decodeQuery = queryString => {
  if (!queryString || typeof queryString !== 'string') return {};

  // Remove "?" inicial se existir
  const cleanQuery = queryString.startsWith('?') ? queryString.slice(1) : queryString;
  if (!cleanQuery) return {};

  const result = {};
  const searchParams = new URLSearchParams(cleanQuery);

  searchParams.forEach((value, key) => {
    try {
      // Tenta fazer parse como JSON (arrays/objetos)
      result[key] = JSON.parse(value);
    } catch {
      // Se falhar, mantém como string
      result[key] = value;
    }
  });

  return result;
};

/**
 * Verifica se uma URL real corresponde a um schema com parâmetros
 * Engenharia reversa de mergeUrlParamsQuery - compara URL processada com schema original
 *
 * @param {string} url - URL real/processada (ex: "https://viacep.com.br/ws/13010111/json/")
 * @param {string} schemaUrl - URL com schema de parâmetros (ex: "https://viacep.com.br/ws/:cep/json/")
 * @returns {boolean} - true se a URL corresponde ao schema, false caso contrário
 *
 * @example
 * matchUrlSchema(
 *   "https://viacep.com.br/ws/13010111/json/",
 *   "https://viacep.com.br/ws/:cep/json/"
 * ) // → true
 *
 * @example
 * matchUrlSchema(
 *   "/v2/entities/123",
 *   "/v2/entities/:_id"
 * ) // → true
 *
 * @example
 * matchUrlSchema(
 *   "/v2/entities/123/details",
 *   "/v2/entities/:_id"
 * ) // → false (segmentos a mais)
 */
const matchUrlSchema = (url, schemaUrl) => {
  if (!url || !schemaUrl) return false;
  if (typeof url !== 'string' || typeof schemaUrl !== 'string') return false;

  // Remove query string de ambas as URLs (tudo após "?")
  const cleanUrl = url.split('?')[0];
  const cleanSchema = schemaUrl.split('?')[0];

  // Remove trailing slashes para comparação consistente
  const normalizedUrl = cleanUrl.replace(/\/$/, '');
  const normalizedSchema = cleanSchema.replace(/\/$/, '');

  // Divide ambas em segmentos
  const urlSegments = normalizedUrl.split('/').filter(segment => segment);
  const schemaSegments = normalizedSchema.split('/').filter(segment => segment);

  // Se o número de segmentos for diferente, não corresponde
  if (urlSegments.length !== schemaSegments.length) {
    return false;
  }

  // Compara segmento por segmento
  for (let i = 0; i < schemaSegments.length; i++) {
    const schemaSegment = schemaSegments[i];
    const urlSegment = urlSegments[i];

    // Se o schema tem um parâmetro (:param), aceita qualquer valor na URL
    if (schemaSegment.startsWith(':')) {
      continue; // Qualquer valor é válido para um parâmetro
    }

    // Se não é parâmetro, deve ser exatamente igual
    if (schemaSegment !== urlSegment) {
      return false;
    }
  }

  return true;
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export {
  getUrl,
  getDomain,
  getUrlEndpoint,
  getQuery,
  getParams,
  removeUrlParams,
  updateUrl,
  updateQuery,
  updateEndpoint,
  mergeUrlParamsQuery,
  matchUrlSchema,
  encodeQuery,
  decodeQuery,
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
